import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';

import { AccountStateService } from './account-state.service';
import { AdminImpersonationService } from './admin-impersonation.service';
import { AuthStoreService } from './auth-store.service';
import { StorageService } from './storage.service';

export type UserTourStatus = 'notStarted' | 'inProgress' | 'completed' | 'dismissed';

export interface UserTourProgress {
  status: UserTourStatus;
  currentStep: number;
  contentVersion: string;
}

export const OWNER_WORKFLOW_TOUR_ID = 'owner-workflow';
export const OWNER_WORKFLOW_TOUR_VERSION = '2026.08.2';
export const OWNER_WORKFLOW_TOUR_STEP_COUNT = 16;

const createInitialProgress = (): UserTourProgress => ({
  status: 'notStarted',
  currentStep: 0,
  contentVersion: OWNER_WORKFLOW_TOUR_VERSION
});

const supportedStatuses: readonly UserTourStatus[] = [
  'notStarted',
  'inProgress',
  'completed',
  'dismissed'
];

@Injectable({ providedIn: 'root' })
export class UserTourService {
  private readonly authStore = inject(AuthStoreService);
  private readonly accountState = inject(AccountStateService);
  private readonly impersonation = inject(AdminImpersonationService);
  private readonly storage = inject(StorageService);

  private readonly progressState = signal<UserTourProgress>(createInitialProgress());
  private readonly openState = signal(false);
  private loadedUserId: string | null = null;

  readonly progress = computed(() => this.progressState());
  readonly status = computed(() => this.progressState().status);
  readonly currentStep = computed(() => this.progressState().currentStep);
  readonly contentVersion = OWNER_WORKFLOW_TOUR_VERSION;
  readonly totalSteps = OWNER_WORKFLOW_TOUR_STEP_COUNT;
  readonly isOpen = computed(() => this.openState());
  readonly canUseTour = computed(
    () => this.authStore.role() === 'owner' && !this.impersonation.isImpersonating()
  );
  readonly canOpenRouteLinks = computed(
    () => this.canUseTour() && this.accountState.isLive()
  );
  readonly canAutoOffer = computed(
    () => this.canUseTour() && this.accountState.isLive() && this.status() === 'notStarted'
  );

  constructor() {
    effect(
      () => {
        const userId = this.authStore.user()?.id ?? null;
        untracked(() => this.loadForUser(userId));
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.canUseTour() && this.openState()) {
          untracked(() => this.openState.set(false));
        }
      },
      { allowSignalWrites: true }
    );
  }

  takeTour(): boolean {
    if (!this.canUseTour()) {
      return false;
    }

    return this.status() === 'inProgress' ? this.resume() : this.restart();
  }

  resume(): boolean {
    if (!this.canUseTour() || this.status() !== 'inProgress') {
      return false;
    }

    this.openState.set(true);
    return true;
  }

  restart(): boolean {
    if (!this.canUseTour()) {
      return false;
    }

    this.save({
      status: 'inProgress',
      currentStep: 0,
      contentVersion: OWNER_WORKFLOW_TOUR_VERSION
    });
    this.openState.set(true);
    return true;
  }

  autoOfferForLiveDashboard(): boolean {
    if (!this.canAutoOffer()) {
      return false;
    }

    this.save({
      status: 'inProgress',
      currentStep: 0,
      contentVersion: OWNER_WORKFLOW_TOUR_VERSION
    });
    this.openState.set(true);
    return true;
  }

  next(): void {
    if (!this.isOpen()) {
      return;
    }

    if (this.currentStep() >= OWNER_WORKFLOW_TOUR_STEP_COUNT - 1) {
      this.finish();
      return;
    }

    this.save({
      ...this.progressState(),
      status: 'inProgress',
      currentStep: this.currentStep() + 1
    });
  }

  back(): void {
    if (!this.isOpen() || this.currentStep() === 0) {
      return;
    }

    this.save({
      ...this.progressState(),
      status: 'inProgress',
      currentStep: this.currentStep() - 1
    });
  }

  pause(): void {
    if (this.status() === 'inProgress') {
      this.save({ ...this.progressState(), status: 'inProgress' });
    }
    this.openState.set(false);
  }

  dismiss(): void {
    this.save({ ...this.progressState(), status: 'dismissed' });
    this.openState.set(false);
  }

  finish(): void {
    this.save({
      ...this.progressState(),
      status: 'completed',
      currentStep: OWNER_WORKFLOW_TOUR_STEP_COUNT - 1
    });
    this.openState.set(false);
  }

  private loadForUser(userId: string | null): void {
    if (this.loadedUserId === userId) {
      return;
    }

    this.loadedUserId = userId;
    this.openState.set(false);

    if (!userId) {
      this.progressState.set(createInitialProgress());
      return;
    }

    const stored = this.storage.getUserTourState<UserTourProgress>(
      userId,
      OWNER_WORKFLOW_TOUR_ID
    );
    const progress = this.normalizeProgress(stored);
    this.progressState.set(progress);
    this.storage.setUserTourState(userId, OWNER_WORKFLOW_TOUR_ID, progress);
  }

  private normalizeProgress(progress: UserTourProgress | null): UserTourProgress {
    if (
      !progress ||
      progress.contentVersion !== OWNER_WORKFLOW_TOUR_VERSION ||
      !supportedStatuses.includes(progress.status) ||
      !Number.isInteger(progress.currentStep) ||
      progress.currentStep < 0 ||
      progress.currentStep >= OWNER_WORKFLOW_TOUR_STEP_COUNT
    ) {
      return createInitialProgress();
    }

    return progress;
  }

  private save(progress: UserTourProgress): void {
    const normalized = this.normalizeProgress(progress);
    this.progressState.set(normalized);

    const userId = this.authStore.user()?.id;
    if (userId) {
      this.storage.setUserTourState(userId, OWNER_WORKFLOW_TOUR_ID, normalized);
    }
  }
}
